import { useRef } from 'react';
import { telemetry } from './telemetry';
import { useRafLoop } from './useRafLoop';

/** Budgets aus der Spezifikation, Abschnitt 8. */
const DRAW_CALL_BUDGET = 150;
const TRIANGLE_BUDGET = 400_000;
const MEMORY_BUDGET_MB = 400;

/**
 * Versteckter Performance-Overlay. Zeigt genau die Werte, gegen die das
 * Budget gemessen wird, und markiert Ueberschreitungen rot.
 */
export function PerfOverlay(): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const accum = useRef(0);

  useRafLoop((dt) => {
    // Der Overlay selbst darf das Messergebnis nicht verfaelschen:
    // fuenf Aktualisierungen pro Sekunde reichen zum Ablesen.
    accum.current += dt;
    if (accum.current < 0.2) return;
    accum.current = 0;

    const p = telemetry.perf;
    const el = ref.current;
    if (!p || !el) return;
    el.innerHTML =
      line('FPS', `${p.fps}`, p.fps >= 55 ? 'ok' : p.fps >= 30 ? '' : 'warn') +
      line('FRAME', `${p.frameMs.toFixed(2)} ms`) +
      line('PHYSIK', `${p.physicsMs.toFixed(2)} ms`) +
      line(
        'DRAWS',
        `${p.drawCalls} / ${DRAW_CALL_BUDGET}`,
        p.drawCalls <= DRAW_CALL_BUDGET ? 'ok' : 'warn',
      ) +
      line(
        'TRIS',
        `${formatK(p.triangles)} / ${formatK(TRIANGLE_BUDGET)}`,
        p.triangles <= TRIANGLE_BUDGET ? 'ok' : 'warn',
      ) +
      line('GEOM/TEX', `${p.geometries} / ${p.textures}`) +
      line('PROGRAMME', `${p.programs}`) +
      line(
        'SPEICHER',
        p.memoryMb > 0 ? `${p.memoryMb} / ${MEMORY_BUDGET_MB} MB` : 'n/v',
        p.memoryMb === 0 ? '' : p.memoryMb <= MEMORY_BUDGET_MB ? 'ok' : 'warn',
      ) +
      line('DPR x SKALA', `${p.pixelRatio} (${p.renderScale.toFixed(2)})`) +
      line('QUALITAET', p.quality) +
      line('TEMPO', `${telemetry.speedKmh.toFixed(1)} km/h`);
  });

  return <div className="perf" ref={ref} aria-hidden="true" />;
}

function line(label: string, value: string, cls = ''): string {
  const padded = label.padEnd(12, ' ');
  return `${padded}<span class="${cls}">${value}</span>\n`;
}

function formatK(n: number): string {
  return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
}
